import { Box, Flex, Group, SimpleGrid } from '@web/components/atoms/Layout'
import type { Comments as CommentsProps } from '@web/graphql-components'
import Comment, { type CommentProps } from './Comment'
import { IconButton, ButtonGroup } from '@web/components/atoms/Button'
import { Icon, LuChevronLeft, LuChevronRight } from '@web/components/atoms/Icons'
import { BRAND_KEY, useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { Skeleton } from '@web/components/atoms/Skeleton'
import { Pagination } from '@web/components/molecules/Pagination'

const PaginatedComments = ({
  comments,
  currentPageNumber,
  finalPageNumber,
  isHorizontalLayout,
  pageSize,
  loadingSlots = 0,
  styles,
  isMobile,
  goToPage,
}: CommentsProps & {
  comments: CommentProps[]
  currentPageNumber: number
  finalPageNumber: number
  pageSize: number
  isHorizontalLayout?: boolean
  isLoading?: boolean
  loadingSlots?: number
  isMobile?: boolean
  goToPage: (pageNumber: number) => void
}) => {
  const arrowColor = useColorWithOpacity(styles?.paginationArrowColor ?? '#000000', '1')
  const hoverArrowColor = useColorWithOpacity(styles?.hoverPaginationArrowColor ?? BRAND_KEY, '1')
  const activePaginationColor = useColorWithOpacity(styles?.activePaginationColor ?? BRAND_KEY, '1')
  const paginationColor = useColorWithOpacity(styles?.paginationColor ?? '#000000', '1')

  const buttonStyles = {
    borderColor: 'transparent',
    color: paginationColor,
    _selected: {
      borderColor: 'transparent',
      fontWeight: 700,
      color: activePaginationColor,
    },
    _hover: {
      borderColor: 'transparent',
      fontWeight: 700,
      color: activePaginationColor,
    },
  }

  const goToPreviousPage = () => {
    const toPage = currentPageNumber - 1
    if (toPage < 1) {
      goToPage(finalPageNumber)

      return
    }

    goToPage(toPage)
  }

  const goToNextPage = () => {
    const toPage = currentPageNumber + 1
    if (toPage > finalPageNumber) {
      goToPage(1)

      return
    }

    goToPage(toPage)
  }

  return (
    <Group w="100%" flexDirection="column" h="100%">
      <Flex
        w="100%"
        h="100%"
        css={{
          '& .c-comments__scroll-button': {
            alignSelf: 'stretch',
            height: 'auto',
            display: finalPageNumber === 1 ? 'none' : 'flex',
            px: '12px',
            flexDirection: 'column',
            justifyContent: 'center',
            cursor: 'pointer',
            color: arrowColor,

            '& svg': {
              color: arrowColor,
            },
          },
        }}
      >
        {isHorizontalLayout && (
          <Box className="c-comments__scroll-button" onClick={goToPreviousPage}>
            <IconButton buttonAppearance="text" _hover={{ borderColor: 'transparent' }}>
              <Icon color={arrowColor} _hover={{ color: hoverArrowColor }}>
                <LuChevronLeft />
              </Icon>
            </IconButton>
          </Box>
        )}
        <SimpleGrid w="100%" gap="16px" columns={isHorizontalLayout ? pageSize : 1}>
          {comments.map((c) => (
            <Comment
              key={c.id}
              {...c}
              styles={{
                width: '100%',
                height: '112px',
              }}
              withPopover={true}
            />
          ))}
          {loadingSlots > 0 && [...Array(loadingSlots)].map((_, i) => <Skeleton key={i} w="100%" h="112px" />)}
        </SimpleGrid>
        {isHorizontalLayout && (
          <Box className="c-comments__scroll-button" onClick={goToNextPage}>
            <IconButton buttonAppearance="text" _hover={{ borderColor: 'transparent' }}>
              <Icon color={arrowColor} _hover={{ color: hoverArrowColor }}>
                <LuChevronRight />
              </Icon>
            </IconButton>
          </Box>
        )}
      </Flex>
      {(!isHorizontalLayout || styles?.options?.includePagination) && (
        <Pagination.Root
          count={pageSize * finalPageNumber}
          pageSize={pageSize}
          page={currentPageNumber}
          onPageChange={(details) => goToPage(details.page)}
          alignSelf={isHorizontalLayout || styles?.options?.includePagination ? 'center' : 'end'}
          siblingCount={isMobile ? 0 : undefined}
        >
          <ButtonGroup variant="ghost" size="sm">
            {!isHorizontalLayout && (
              <IconButton
                buttonAppearance="text"
                color={arrowColor}
                _hover={{ color: hoverArrowColor, borderColor: 'transparent' }}
                onClick={goToPreviousPage}
              >
                <LuChevronLeft />
              </IconButton>
            )}
            {styles?.options?.includePagination && (
              <Pagination.Context>
                {({ pages }) =>
                  pages.map((page, index) =>
                    page.type === 'page' ? (
                      <Pagination.Item key={index} {...page} onClick={() => goToPage(page.value)}>
                        <IconButton
                          buttonAppearance="text"
                          {...buttonStyles}
                          aria-selected={page.value === currentPageNumber}
                        >
                          {page.value}
                        </IconButton>
                      </Pagination.Item>
                    ) : (
                      <Pagination.Ellipsis key={index} index={index}>
                        <IconButton
                          buttonAppearance="text"
                          color={paginationColor}
                          _hover={{ color: paginationColor, borderColor: 'transparent' }}
                        >
                          ...
                        </IconButton>
                      </Pagination.Ellipsis>
                    ),
                  )
                }
              </Pagination.Context>
            )}
            {!isHorizontalLayout && (
              <IconButton
                buttonAppearance="text"
                color={arrowColor}
                _hover={{ color: hoverArrowColor, borderColor: 'transparent' }}
                onClick={goToNextPage}
              >
                <LuChevronRight />
              </IconButton>
            )}
          </ButtonGroup>
        </Pagination.Root>
      )}
    </Group>
  )
}

export default PaginatedComments
